import { type IAggregation, type IAggregationResponse } from '@inpart-io/devtools-iap';
import { type MutationTree } from 'vuex';

import { LeadDealType } from '@/constants/deal-types.constants';

import { type IAggregationByOriginEntity, type IAggregationByStage, type IAggregationByStatus, type IAggregationByType, type ILeadState } from './types';

export const RECEIVE_LEAD_AGGREGATION_BY_TYPE = 'RECEIVE_LEAD_AGGREGATION_BY_TYPE';
export const RECEIVE_LEAD_AGGREGATION_BY_BRIEF_TYPE = 'RECEIVE_LEAD_AGGREGATION_BY_BRIEF_TYPE';
export const RECEIVE_LEAD_AGGREGATION_BY_STAGE = 'RECEIVE_LEAD_AGGREGATION_BY_STAGE';
export const RECEIVE_LEAD_AGGREGATION_BY_STATUS = 'RECEIVE_LEAD_AGGREGATION_BY_STATUS';
export const RECEIVE_LEAD_AGGREGATION_BY_BRIEF_STATUS = 'RECEIVE_LEAD_AGGREGATION_BY_BRIEF_STATUS';
export const RECEIVE_LEAD_AGGREGATION_BY_ORIGIN_ENTITY = 'RECEIVE_LEAD_AGGREGATION_BY_ORIGIN_ENTITY';
export const UPDATE_AGGREGATION_OF_STATUS = 'UPDATE_AGGREGATION_OF_STATUS';
export const UPDATE_AGGREGATION_OF_TYPE = 'UPDATE_AGGREGATION_OF_TYPE';

function getStatusAggregation(aggregation: IAggregation): IAggregationByStatus {
    const statusAggregation: IAggregationByStatus = { byStatus: {}, totalCount: 0 };

    for (const statusGroup of aggregation.groups) {
        const statusKey = statusGroup.keyAsString.toUpperCase();
        statusAggregation.byStatus[statusKey] = statusGroup.objCount;
    }
    return statusAggregation;
}
function getStageAggregation(aggregation: IAggregation): IAggregationByStatus {
    const stageAggregation: IAggregationByStage = { byStage: {}, totalCount: 0 };
    for (const stageGroup of aggregation.groups) {
        const stageKey = stageGroup.keyAsString.toUpperCase();
        stageAggregation.byStage[stageKey] = stageGroup.objCount;
    }
    return stageAggregation;
}
function getTypeAggregation(aggregation: IAggregation, aggregationObjCount: number): IAggregationByStatus {
    const typeAggregation: IAggregationByType = { byType: {}, totalCount: 0 };
    let totalObjCount = 0;
    for (const typeGroup of aggregation.groups) {
        const typeKey = typeGroup.keyAsString.toUpperCase();
        typeAggregation.byType[typeKey] = typeGroup.objCount;
        totalObjCount += typeGroup.objCount;
    }
    // The objects not linked to a type are simple leads
    typeAggregation.byType[LeadDealType.dobjId.toString()] = aggregationObjCount - totalObjCount;
    typeAggregation.totalCount = aggregationObjCount;
    return typeAggregation;
}
function getOriginEntityAggregation(aggregation: IAggregation): IAggregationByOriginEntity {
    const originEntityAggregation: IAggregationByOriginEntity = { byOriginEntity: {}, totalCount: 0 };

    for (const originEntityGroup of aggregation.groups) {
        const originEntityKey = originEntityGroup.keyAsString.toUpperCase();
        originEntityAggregation.byOriginEntity[originEntityKey] = originEntityGroup.objCount;
    }
    return originEntityAggregation;
}

export const mutations: MutationTree<ILeadState> = {
    [RECEIVE_LEAD_AGGREGATION_BY_TYPE](_state, { key, aggregationResult }: { key?: string; aggregationResult?: IAggregationResponse }) {
        let typeAggregation: IAggregationByType = { byType: {}, totalCount: 0 };
        if (aggregationResult?.results?.[0] !== undefined) {
            typeAggregation = getTypeAggregation(aggregationResult.results[0], aggregationResult.objCount);
            _state.aggregationByType[key] = Object.assign({}, _state.aggregationByType[key], typeAggregation);
        } else {
            _state.aggregationByType[key] = Object.assign({}, _state.aggregationByType[key], typeAggregation);
        }
    },
    [RECEIVE_LEAD_AGGREGATION_BY_BRIEF_TYPE](_state, { aggregationResult, briefs }: { aggregationResult?: IAggregationResponse; briefs?: number[] }) {
        if (aggregationResult !== undefined) {
            // Set default initial state
            for (const briefId of briefs) {
                const key = `brief-${briefId}`;
                _state.aggregationByType[key] = Object.assign({}, _state.aggregationByType[key], { byType: {}, totalCount: 0 });
            }

            if (aggregationResult?.results?.[0] !== undefined) {
                for (const brief of aggregationResult.results[0].groups) {
                    const key = `brief-${brief.keyAsString}`;

                    let typeAggregation: IAggregationByType = { byType: {}, totalCount: 0 };
                    if (brief?.aggregations[0] !== undefined) {
                        typeAggregation = getTypeAggregation(brief.aggregations[0], brief.objCount);
                    }
                    _state.aggregationByType[key] = Object.assign({}, _state.aggregationByType[key], typeAggregation);
                }
            }
        }
    },
    [RECEIVE_LEAD_AGGREGATION_BY_STATUS](_state, { key, aggregationResult }: { key?: string; aggregationResult?: IAggregationResponse }) {
        let byStatus: IAggregationByStatus = { byStatus: {}, totalCount: 0 };
        if (aggregationResult?.results[0] !== undefined) {
            byStatus = getStatusAggregation(aggregationResult.results[0]);
            byStatus.totalCount = aggregationResult.objCount;
            _state.aggregationByStatus[key] = Object.assign({}, _state.aggregationByStatus[key], byStatus);
        } else {
            _state.aggregationByStatus[key] = Object.assign({}, _state.aggregationByStatus[key], byStatus);
        }
    },
    [RECEIVE_LEAD_AGGREGATION_BY_STAGE](_state, { key, aggregationResult }: { key?: string; aggregationResult?: IAggregationResponse }) {
        let byStage: IAggregationByStage = { byStage: {}, totalCount: 0 };
        if (aggregationResult?.results[0] !== undefined) {
            byStage = getStageAggregation(aggregationResult.results[0]);
            byStage.totalCount = aggregationResult.objCount;
            _state.aggregationByStage[key] = Object.assign({}, _state.aggregationByStage[key], byStage);
        } else {
            _state.aggregationByStage[key] = Object.assign({}, _state.aggregationByStage[key], byStage);
        }
    },
    [RECEIVE_LEAD_AGGREGATION_BY_BRIEF_STATUS](_state, { aggregationResult, briefs }: { aggregationResult?: IAggregationResponse; briefs?: number[] }) {
        if (aggregationResult !== undefined && aggregationResult !== null) {
            // Set default initial state
            for (const briefId of briefs) {
                const key = `brief-${briefId}`;
                _state.aggregationByStatus[key] = Object.assign({}, _state.aggregationByStatus[key], { byStatus: {}, totalCount: 0 });
            }

            if (aggregationResult?.results?.[0] !== undefined) {
                for (const brief of aggregationResult.results[0].groups) {
                    const key = `brief-${brief.keyAsString}`;

                    let byStatus: IAggregationByStatus = { byStatus: {}, totalCount: 0 };
                    if (brief?.aggregations?.[0] !== undefined) {
                        byStatus = getStatusAggregation(brief.aggregations[0]);
                        byStatus.totalCount = brief.objCount;
                    }
                    _state.aggregationByStatus[key] = Object.assign({}, _state.aggregationByStatus[key], byStatus);
                }
            }
        }
    },
    [RECEIVE_LEAD_AGGREGATION_BY_ORIGIN_ENTITY](_state, { key, aggregationResult }: { key?: string; aggregationResult?: IAggregationResponse }) {
        let byOriginEntity: IAggregationByOriginEntity = { byOriginEntity: {}, totalCount: 0 };

        if (aggregationResult?.results[0] !== undefined) {
            byOriginEntity = getOriginEntityAggregation(aggregationResult.results[0]);
            byOriginEntity.totalCount = aggregationResult.objCount;
            _state.aggregationByOriginEntity[key] = Object.assign({}, _state.aggregationByOriginEntity[key], byOriginEntity);
        } else {
            _state.aggregationByOriginEntity[key] = Object.assign({}, _state.aggregationByOriginEntity[key], byOriginEntity);
        }
    },
    [UPDATE_AGGREGATION_OF_STATUS](_state, { status, key, count }: { status?: string; key?: string; count?: number }) {
        if (_state.aggregationByStatus[key]?.byStatus === undefined || _state.aggregationByStatus[key]?.byStatus === null) {
            _state.aggregationByStatus[key] = { byStatus: {}, totalCount: 0 };
        }
        const countByStatus = _state.aggregationByStatus[key].byStatus[status] ?? 0;
        const currentCountByStatus = countByStatus + count;

        const currentTotalCount = _state.aggregationByStatus[key].totalCount + count;

        _state.aggregationByStatus[key].byStatus[status] = currentCountByStatus >= 0 ? currentCountByStatus : 0;
        _state.aggregationByStatus[key].totalCount = currentTotalCount >= 0 ? currentTotalCount : 0;
    },
    [UPDATE_AGGREGATION_OF_TYPE](_state, { type, key, count }: { type?: string; key?: string; count?: number }) {
        if (_state.aggregationByType[key]?.byType === undefined || _state.aggregationByType[key]?.byType === null) {
            _state.aggregationByType[key] = { byType: {}, totalCount: 0 };
        }
        const countByType = _state.aggregationByType[key].byType[type] ?? 0;
        const currentCountByType = countByType + count;

        const currentTotalCount = _state.aggregationByType[key].totalCount + count;

        _state.aggregationByType[key].byType[type] = currentCountByType >= 0 ? currentCountByType : 0;
        _state.aggregationByType[key].totalCount = currentTotalCount >= 0 ? currentTotalCount : 0;
    }
};
